import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../lib/auth.jsx';
import { useEffectiveRole } from '../../lib/license.jsx';
import { usePwa } from '../../lib/pwa.jsx';
import { useClock, formatInTzParts } from '../../lib/utcClock.js';
import { DEFAULT_TIMEZONE } from '../../lib/timezones.js';

const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';

const MAIN_NAV = [
  { to: '/dashboard',    label: 'Dashboard', icon: 'fa-gauge-high' },
  { to: '/map',          label: 'Map',       icon: 'fa-map-location-dot' },
  { to: '/style-editor', label: 'Editor',    icon: 'fa-pen-ruler' },
  { to: '/datasets',     label: 'Datasets',  icon: 'fa-layer-group' },
  { to: '/api-keys',     label: 'API',       icon: 'fa-key' }
];

const ADMIN_NAV = [
  { to: '/users',     label: 'Users',     icon: 'fa-users' },
  { to: '/settings',  label: 'Settings',  icon: 'fa-gear' },
  { to: '/logs',      label: 'Logs',      icon: 'fa-clock-rotate-left' }
];

function NavItem({ to, label, icon }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition',
          isActive
            ? 'bg-ink-900 text-white'
            : 'text-ink-700 hover:bg-ink-100'
        ].join(' ')
      }
    >
      <i className={`fa-solid ${icon} w-5 text-center`} />
      <span>{label}</span>
    </NavLink>
  );
}

function RoleBadge({ role }) {
  const tone =
    role === 'admin'
      ? 'bg-brand-100 text-brand-700'
      : 'bg-ink-100 text-ink-600';
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${tone}`}>
      {role}
    </span>
  );
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  // RoleBadge reflects effectiveRole (admin demoted to user under a
  // restrictive licence) so the operator immediately sees they're in
  // read-only mode. The licence banner above provides the WHY.
  const effectiveRole = useEffectiveRole();
  const {
    installable, updateAvailable, iosInstallAvailable,
    promptInstall, applyUpdate
  } = usePwa();
  const [iosHintOpen, setIosHintOpen] = useState(false);
  const now = useClock();
  const tz = user?.timezone || DEFAULT_TIMEZONE;
  const clockParts = formatInTzParts(now, tz);

  return (
    <aside className="flex w-[260px] shrink-0 flex-col border-r border-ink-100 bg-white">
      {/* Brand */}
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-ink-50 ring-1 ring-ink-100">
            <img src="/icons/logo.svg" alt="" className="h-8 w-8" />
          </div>
          <div className="min-w-0 leading-tight">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">MVT ADMS</div>
            <div className="truncate text-lg font-bold text-ink-900">Admin Console</div>
          </div>
        </div>
        <div className="mt-2 text-xs text-ink-500">Map Vector Tile Server</div>
      </div>

      <div className="border-t border-ink-100" />

      {/* User */}
      <div className="flex items-center gap-3 px-5 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-ink-100 text-ink-500">
          <i className="fa-solid fa-user text-sm" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-ink-900">
            {user?.fullname || user?.username || '—'}
          </div>
          {effectiveRole && <RoleBadge role={effectiveRole} />}
        </div>
      </div>

      <div className="border-t border-ink-100" />

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        <ul className="space-y-0.5">
          {MAIN_NAV.map((item) => (
            <li key={item.to}><NavItem {...item} /></li>
          ))}
        </ul>

        <div className="my-3 border-t border-ink-100" />

        <ul className="space-y-0.5">
          {ADMIN_NAV.map((item) => (
            <li key={item.to}><NavItem {...item} /></li>
          ))}

          {/* PWA install / update — appears under Logs only when the
              browser has offered install (beforeinstallprompt), a new
              service worker is waiting, or we're on iOS Safari where
              installability can't be programmatically detected. Amber
              outline marks it as a non-navigation action. */}
          {(updateAvailable || installable || iosInstallAvailable) && (
            <li>
              <button
                onClick={
                  updateAvailable ? applyUpdate
                  : installable   ? promptInstall
                  : () => setIosHintOpen(true)
                }
                className="flex w-full items-center gap-3 rounded-lg border-2 border-amber-400 bg-amber-50/40 px-3 py-2 text-sm font-semibold text-amber-800 transition hover:bg-amber-50"
                title={
                  updateAvailable ? 'A new version is ready — reload to activate'
                  : installable   ? 'Add MVT ADMS to your device'
                  :                 'How to install on iOS — tap for instructions'
                }
              >
                <i
                  className={`fa-solid w-5 text-center ${
                    updateAvailable ? 'fa-arrow-rotate-right'
                    : installable   ? 'fa-cloud-arrow-down'
                    :                 'fa-arrow-up-from-bracket'
                  }`}
                />
                <span className="flex-1 text-left">{
                  updateAvailable ? 'Update PWA'
                  : installable   ? 'Install PWA'
                  :                 'Install on iOS'
                }</span>
                {updateAvailable && (
                  <span
                    className="h-2 w-2 rounded-full bg-amber-500 animate-pulse"
                    aria-hidden="true"
                  />
                )}
              </button>
            </li>
          )}
        </ul>

        <div className="my-3 border-t border-ink-100" />

        <ul className="space-y-0.5">
          <li>
            <button
              onClick={logout}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-ink-700 transition hover:bg-ink-100"
            >
              <i className="fa-solid fa-right-from-bracket w-5 text-center" />
              <span>Sign Out</span>
            </button>
          </li>
        </ul>
      </nav>

      {/* Live clock — body formatted in the user's chosen timezone, short zone
          abbreviation rendered as a dimmed suffix pill. */}
      <div className="px-4 pt-2 pb-2 flex justify-center">
        <div
          className="inline-flex items-center gap-2 rounded-full bg-ink-900 px-3 py-1.5 font-mono text-xs text-white"
          title={`Timezone: ${tz}`}
        >
          <i className="fa-solid fa-circle text-[6px] text-emerald-400" />
          <span>{clockParts.body}</span>
          <span className="text-ink-300">{clockParts.tz}</span>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-2 border-t border-ink-100 px-4 py-3 text-[11px] text-ink-500">
        <span>&copy; {now.getUTCFullYear()} MicroGIS&trade;</span>
        <span className="font-mono">Build v{APP_VERSION}</span>
      </div>

      {iosHintOpen && <IosInstallHint onClose={() => setIosHintOpen(false)} />}
    </aside>
  );
}

// iOS Safari can't fire `beforeinstallprompt`, so we explain the three
// manual taps. Rendered as a modal dialog; backdrop click + ESC close.
function IosInstallHint({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 p-4"
      onClick={onClose}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="max-w-sm rounded-xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center gap-2">
          <i className="fa-solid fa-mobile-screen-button text-amber-600" />
          <h4 className="text-base font-semibold text-ink-900">Install on iOS</h4>
        </div>
        <p className="mb-4 text-sm text-ink-600">
          Safari on iOS doesn't expose an install prompt to the page.
          Add MVT ADMS to your home screen manually:
        </p>
        <ol className="space-y-2 text-sm text-ink-800">
          <li className="flex gap-2">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ink-100 text-xs font-semibold">1</span>
            <span>Tap the <i className="fa-solid fa-arrow-up-from-bracket mx-1" /><b>Share</b> button in Safari's bottom bar.</span>
          </li>
          <li className="flex gap-2">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ink-100 text-xs font-semibold">2</span>
            <span>Scroll and pick <b>Add to Home Screen</b>.</span>
          </li>
          <li className="flex gap-2">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ink-100 text-xs font-semibold">3</span>
            <span>Confirm the name and tap <b>Add</b>.</span>
          </li>
        </ol>
        <div className="mt-5 flex justify-end">
          <button className="btn-primary" onClick={onClose}>Got it</button>
        </div>
      </div>
    </div>
  );
}
